import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Download, Loader2 } from 'lucide-react';
import { Modal, ModalMainContent } from '../ui/Modal';
import { Button } from '../ui/Button';
import Select from '../ui/Select';
import { apiService } from '../../services/api';

const Intro = styled.p`
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.5;
  color: ${(p) => p.theme.mutedForeground};
`;

const InfoNote = styled.p`
  margin: 0 0 20px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.muted};
  font-size: 13px;
  line-height: 1.5;
  color: ${(p) => p.theme.mutedForeground};
`;

const EmptyNote = styled.p`
  margin: 0 0 20px;
  font-size: 13px;
  line-height: 1.5;
  color: ${(p) => p.theme.mutedForeground};
`;

const PropertyName = styled.strong`
  color: ${(p) => p.theme.foreground};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${(p) => p.theme.border};
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  color: ${(p) => p.theme.mutedForeground};
`;

export interface PropertyReportPeriod {
  month: number;
  year: number;
}

interface AvailablePeriod extends PropertyReportPeriod {
  label: string;
}

interface PropertyReportPeriodModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string | null;
  propertyName?: string;
  onDownload: (period: PropertyReportPeriod) => void | Promise<void>;
  isDownloading?: boolean;
}

function periodKey(period: PropertyReportPeriod): string {
  return `${period.year}-${period.month}`;
}

function parsePeriodKey(key: string): PropertyReportPeriod | null {
  const [yearStr, monthStr] = key.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (!year || !month || month < 1 || month > 12) return null;
  return { year, month };
}

const PropertyReportPeriodModal: React.FC<PropertyReportPeriodModalProps> = ({
  isOpen,
  onClose,
  propertyId,
  propertyName,
  onDownload,
  isDownloading = false,
}) => {
  const [periods, setPeriods] = useState<AvailablePeriod[]>([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState('');

  useEffect(() => {
    if (!isOpen || !propertyId) {
      setPeriods([]);
      setSelectedKey('');
      setLoadError(null);
      return;
    }

    let cancelled = false;
    setLoadingPeriods(true);
    setLoadError(null);

    void apiService
      .getPropertyReportAvailablePeriods(propertyId)
      .then((response) => {
        if (cancelled) return;
        const data = response.data?.data || response.data || [];
        const available = Array.isArray(data) ? data : [];
        setPeriods(available);
        setSelectedKey(available.length ? periodKey(available[0]) : '');
      })
      .catch((err: any) => {
        if (cancelled) return;
        setPeriods([]);
        setSelectedKey('');
        setLoadError(err?.message || 'Could not load available report periods.');
      })
      .finally(() => {
        if (!cancelled) setLoadingPeriods(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, propertyId]);

  const periodOptions = useMemo(
    () => periods.map((period) => ({ value: periodKey(period), label: period.label })),
    [periods],
  );

  const selectedPeriod = useMemo(() => parsePeriodKey(selectedKey), [selectedKey]);

  const handleDownload = () => {
    if (!selectedPeriod) return;
    void onDownload(selectedPeriod);
  };

  const hasPeriods = periods.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Download property report"
      size="sm"
    >
      <ModalMainContent>
        <Intro>
          Choose a reporting period for{' '}
          {propertyName ? <PropertyName>{propertyName}</PropertyName> : 'this property'}.
        </Intro>

        <InfoNote>
          Only months with rent collection data are listed. Months without generated rent schedules
          are not available to download.
        </InfoNote>

        {loadingPeriods ? (
          <LoadingRow>
            <Loader2 size={16} className="spinner" />
            Loading available periods…
          </LoadingRow>
        ) : loadError ? (
          <EmptyNote>{loadError}</EmptyNote>
        ) : !hasPeriods ? (
          <EmptyNote>
            No report periods are available yet. Generate rent schedules for this property to
            unlock monthly reports.
          </EmptyNote>
        ) : (
          <Select
            label="Reporting period"
            value={selectedKey}
            onChange={(value) => setSelectedKey(String(value))}
            options={periodOptions}
            fullWidth
          />
        )}

        <ModalFooter>
          <Button variant="outline" onClick={onClose} disabled={isDownloading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDownload}
            disabled={isDownloading || loadingPeriods || !hasPeriods || !selectedPeriod}
          >
            {isDownloading ? (
              <>
                <Loader2 size={16} className="spinner" />
                Building…
              </>
            ) : (
              <>
                <Download size={16} />
                Download report
              </>
            )}
          </Button>
        </ModalFooter>
      </ModalMainContent>
    </Modal>
  );
};

export default PropertyReportPeriodModal;
